import type { DragContext } from '../canvas/drag'

/* Captured posters are rasterised at twice their design size, so a downloaded or uploaded
   poster is usable at more than preview size. */
export const posterCaptureScale = 2;

async function saveBlob(
	blob: Blob,
	suggestedName: string,
	types: { description: string; accept: Record<string, string[]> }[],
)
{
	if (window.showSaveFilePicker) {
		try {
			const handle   = await window.showSaveFilePicker({ suggestedName, types });
			const writable = await handle.createWritable();
			await writable.write(blob);
			await writable.close();
		} catch (e) {
			if ((e as DOMException).name !== 'AbortError') throw e;
		}
	} else {
		const url = URL.createObjectURL(blob);
		const a   = Object.assign(document.createElement('a'), { href: url, download: suggestedName });
		document.body.appendChild(a);
		a.click();
		setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url) }, 100);
	}
}

// Inline <image> hrefs as data URLs. An SVG loaded via img.src can't fetch
// external resources, so the asset-store URLs that image objects now use would
// render blank otherwise. (Fonts are embedded the same way below.)
async function inlineImages(clone: SVGSVGElement)
{
	const XLINK = 'http://www.w3.org/1999/xlink';
	const images = Array.from(clone.querySelectorAll('image'));
	await Promise.all(images.map(async img => {
		const href = img.getAttribute('href') ?? img.getAttributeNS(XLINK, 'href');
		if (!href || href.startsWith('data:')) return;
		try {
			const blob    = await fetch(href).then(r => r.blob());
			const dataUrl = await blobToDataUrl(blob);
			img.setAttribute('href', dataUrl);
			img.setAttributeNS(XLINK, 'href', dataUrl);
		} catch { /* leave href as-is; that image will be missing */ }
	}))
}

async function inlineFonts(clone: SVGSVGElement)
{
	const fontLinks = document.querySelectorAll('link[rel=stylesheet][href*="fonts.googleapis.com"]')
	if (!fontLinks.length) return;

	try {
		const cssArr = await Promise.all(Array.from(fontLinks).map(l => fetch((l as HTMLLinkElement).href).then(r => r.text())));
		let css = cssArr.join('\n');

		const fontUrls = [...new Set(
			[...css.matchAll(/url\(['"]?(https:\/\/fonts\.gstatic\.com[^'")]+)['"]?\)/g)].map(m => m[1])
		)]

		const dataUrls = await Promise.all(fontUrls.map(async url => {
			const blob = await fetch(url).then(r => r.blob());
			return {url, dataUrl: await blobToDataUrl(blob)};
		}))

		for (const { url, dataUrl } of dataUrls)
			css = css.replaceAll(`url(${url})`, `url(${dataUrl})`);

		const styleEl = document.createElementNS('http://www.w3.org/2000/svg', 'style');
		styleEl.textContent = css;
		clone.insertBefore(styleEl, clone.firstChild);
	} catch { /* render without custom fonts */ }
}

function blobToDataUrl(blob: Blob): Promise<string>
{
	return new Promise<string>((resolve, reject) => {
		const reader = new FileReader();
		reader.onload  = () => resolve(reader.result as string);
		reader.onerror = reject;
		reader.readAsDataURL(blob);
	});
}

/*
	Rasterise a poster's live <svg> to a JPEG. Everything the SVG refers to (images, fonts) has to be
	embedded first: once it is handed to an <img> as a blob url it can no longer fetch anything.
	This is why the poster can't simply be screenshotted from the DOM.
*/
export async function posterToJpeg(svgEl: SVGSVGElement, scale: number): Promise<Blob | null>
{
	const w = parseInt(svgEl.getAttribute('width') ?? '0') * scale;
	const h = parseInt(svgEl.getAttribute('height') ?? '0') * scale;

	const clone = svgEl.cloneNode(true) as SVGSVGElement;
	clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');

	/* The clone must carry the *scaled* size, not the source's: an <img> rasterises an SVG at its
	   intrinsic size, so drawing a 400x600 image into an 800x1200 canvas would upscale a small bitmap
	   rather than render the vectors at the larger size. The viewBox is left alone, so the content
	   scales to fit. */
	clone.setAttribute('width', String(w));
	clone.setAttribute('height', String(h));

	/* The selection box and resize handles live in the editable SVG's top layer, and are not part of
	   the poster. */
	clone.querySelector('[data-handles]')?.remove();

	await inlineImages(clone);
	await inlineFonts(clone);

	const svgStr  = new XMLSerializer().serializeToString(clone);
	const svgBlob = new Blob([svgStr], { type: 'image/svg+xml;charset=utf-8' });
	const svgUrl  = URL.createObjectURL(svgBlob);

	return await new Promise<Blob | null>(resolve => {
		const img = new Image();
		img.onload = () => {
			const canvas2d = document.createElement('canvas');
			canvas2d.width  = w;
			canvas2d.height = h;
			canvas2d.getContext('2d')!.drawImage(img, 0, 0, w, h);
			URL.revokeObjectURL(svgUrl);
			canvas2d.toBlob(resolve, 'image/jpeg', 0.95);
		}
		img.onerror = () => { URL.revokeObjectURL(svgUrl); resolve(null) };
		img.src = svgUrl;
	})
}

export async function handleRender(dragCtx: DragContext)
{
	const svgEl = dragCtx.svgRef();
	if (!svgEl) return;

	const jpegBlob = await posterToJpeg(svgEl,posterCaptureScale);

	if (jpegBlob)
		await saveBlob(jpegBlob, 'poster.jpg', [{ description: 'JPEG Image', accept: { 'image/jpeg': ['.jpg', '.jpeg'] } }]);
}
